import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Users, AlertTriangle, UserPlus } from "lucide-react";
import { usersAPI } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const SuperAdminDashboard = () => {
  // Fetch dashboard data
  const { isPending: statsLoading } = useQuery({
    queryKey: ["systemStats"],
    queryFn: () => usersAPI.getSystemStats(),
    select: (data) => data.data.stats,
  });

  const { isPending: activitiesLoading } = useQuery({
    queryKey: ["recentActivities"],
    queryFn: () => usersAPI.getRecentActivities({ limit: 10 }),
    select: (data) => data.data.activities,
  });

  // Fetch all users data for statistics
  const { data: allUsersData, isPending: usersLoading } = useQuery({
    queryKey: ["allUsersStats"],
    queryFn: async () => {
      const response = await usersAPI.getUsers({
        limit: 1000, // Get a large number for statistics
      });
      return response?.data?.data?.users || [];
    },
    select: (data) => data || [],
  });

  const isLoading = statsLoading || activitiesLoading || usersLoading;

  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading system dashboard..." />;
  }

  // Dashboard is always accessible to Super Admin users
  // No permission check needed for the main dashboard

  // Calculate real statistics from user data
  const allUsers = allUsersData || [];
  const totalActiveUsers = allUsers.filter(
    (u) => u.isActive && u.registrationStatus !== "pending_verification"
  ).length;
  const pendingUserApprovals = allUsers.filter(
    (u) => u.registrationStatus === "pending_verification"
  ).length;

  const systemMetrics = [
    {
      name: "Total Active Users",
      value: totalActiveUsers.toString(),
      change: "+3 this week",
      icon: Users,
      color: "primary",
      href: "/admin/users",
      requiredPermission: "view_users",
    },
    {
      name: "Pending User Approvals",
      value: pendingUserApprovals.toString(),
      change: "Requires attention",
      icon: UserPlus,
      color: "warning",
      href: "/admin/users?filter=pending",
      requiredPermission: "view_users",
    },
  ].filter(() => true);

  // Helper function to get display name for roles
  const getRoleDisplayName = (role) => {
    const roleNames = {
      issuer: "Issuer",
      facility_agent: "Facility Agent",
      custodian: "Custodian",
      super_admin: "Super Admin",
      investor: "Investor",
      primary_subscriber: "Primary Subscriber",
      trustee: "Trustee",
    };
    return roleNames[role] || role;
  };

  // Calculate real user distribution from actual data
  const calculateUserDistribution = () => {
    const roleCounts = {
      Issuer: 0,
      "Facility Agent": 0,
      Custodian: 0,
      "Super Admin": 0,
      Investor: 0,
      "Primary Subscriber": 0,
      Trustee: 0,
    };

    // Count users by role
    allUsers.forEach((user) => {
      const roleName = getRoleDisplayName(user.role);
      if (roleCounts.hasOwnProperty(roleName)) {
        roleCounts[roleName]++;
      }
    });

    const totalUsers = allUsers.length;

    // Convert to array with percentages
    return Object.entries(roleCounts).map(([role, count]) => ({
      role,
      count,
      percentage: totalUsers > 0 ? Math.round((count / totalUsers) * 100) : 0,
    }));
  };

  const userDistribution = calculateUserDistribution();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div>
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Super Admin Dashboard
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                System administration and platform management overview
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* System Metrics */}
        <div className="grid grid-cols-1 gap-6 mb-4 sm:grid-cols-2">
          {systemMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <Link
                key={metric.name}
                to={metric.href}
                className="transition-shadow card hover:shadow-lg"
              >
                <div className="card-body">
                  <div className="flex items-center">
                    <div
                      className={`flex-shrink-0 p-3 rounded-lg bg-${metric.color}-100`}
                    >
                      <Icon className={`h-6 w-6 text-${metric.color}-600`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-secondary-600">
                        {metric.name}
                      </p>
                      <p className="text-2xl font-bold text-secondary-900">
                        {metric.value}
                      </p>
                      <p className="mt-1 text-xs text-secondary-500">
                        {metric.change}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* User Distribution */}
        <div className="mb-4 card">
          <div className="flex items-center justify-between card-header">
            <h3>User Distribution</h3>
            <Link
              to="/admin/users"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Manage Users
            </Link>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {userDistribution.map((role) => (
                <div
                  key={role.role}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-900">
                        {role.role}
                      </p>
                      <div className="w-full h-2 mt-1 rounded-full bg-secondary-200">
                        <div
                          className="h-2 rounded-full bg-primary-600"
                          style={{ width: `${role.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="text-sm font-bold text-secondary-900">
                      {role.count}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {role.percentage}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Critical Alerts */}
        {pendingUserApprovals > 0 && (
          <div className="mb-4 card border-warning-200 bg-warning-50">
            <div className="card-body">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-warning-600 mt-0.5" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-warning-800">
                    Attention Required
                  </h4>
                  <p className="mt-1 text-sm text-warning-700">
                    There are {pendingUserApprovals} pending user approval
                    requests that require immediate attention.
                  </p>
                  <div className="mt-3">
                    <Link
                      to="/admin/users?filter=pending"
                      className="text-sm font-medium text-warning-800 hover:text-warning-900"
                    >
                      Review User Approvals →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
